import React from 'react';
import { motion } from 'framer-motion';
import { FileText, AlertTriangle, CheckCircle, XCircle, Scale, UserX } from 'lucide-react';

const Terms: React.FC = () => {
  const sections = [
    {
      icon: CheckCircle,
      title: 'Acceptable Use',
      content: [
        'Use TypeRush for personal skill improvement and entertainment',
        'Participate in battles and competitions fairly and respectfully',
        'Respect other users and maintain a positive community',
        'Report bugs, vulnerabilities, or inappropriate content',
        'Provide accurate information during account creation',
        'Keep your login credentials secure and confidential'
      ]
    },
    {
      icon: XCircle,
      title: 'Prohibited Activities',
      content: [
        'Using bots, scripts, or automation tools to gain unfair advantages',
        'Manipulating or cheating the typing test or battle systems',
        'Harassing, threatening, or abusing other users',
        'Sharing offensive, inappropriate, or illegal content',
        'Creating multiple accounts to exploit features or rewards',
        'Attempting to hack, disrupt, or damage the platform',
        'Impersonating other users, staff, or entities',
        'Commercial use without explicit written permission'
      ]
    },
    {
      icon: UserX,
      title: 'Account Termination',
      content: [
        'We reserve the right to suspend or terminate accounts that violate these terms',
        'Violations include cheating, harassment, or any prohibited activities',
        'Repeated violations may result in permanent bans',
        'You may delete your account at any time through settings',
        'Terminated accounts forfeit all progress, premium status, and virtual items',
        'Appeals can be submitted within 30 days of termination'
      ]
    },
    {
      icon: Scale,
      title: 'Intellectual Property',
      content: [
        'TypeRush owns all platform content, design, and source code',
        'Users retain ownership of their custom text submissions',
        'By submitting custom text, you grant us a license to use it',
        'Typing test results and statistics are considered platform data',
        'Do not reproduce, distribute, or modify our platform without permission',
        'Trademarks and logos are protected and may not be used without authorization'
      ]
    }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-12"
      >
        <h1 className="text-5xl md:text-6xl font-bold mb-6">
          Terms of <span className="text-accent">Service</span>
        </h1>
        <p className="text-textMuted text-lg">
          Last updated: January 24, 2026
        </p>
        <p className="text-textMuted mt-4 max-w-2xl mx-auto">
          By using TypeRush, you agree to be bound by these Terms of Service. Please read 
          them carefully before using our platform.
        </p>
      </motion.div>

      {/* Agreement Notice */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="bg-accent/10 border border-accent/20 rounded-xl p-6 mb-12"
      >
        <h2 className="text-2xl font-bold mb-4 text-accent flex items-center">
          <FileText className="w-6 h-6 mr-2" />
          Agreement to Terms
        </h2>
        <p className="text-textMuted mb-4">
          By accessing or using TypeRush, you agree to:
        </p>
        <ul className="space-y-2 text-textMuted">
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            Comply with all terms and conditions outlined in this document
          </li>
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            Follow our Community Guidelines and Fair Play policies
          </li>
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            Accept updates to these terms as they are posted
          </li>
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            Be at least 13 years old (or have parental consent)
          </li>
        </ul>
      </motion.div>

      {/* Main Sections */}
      <div className="space-y-8 mb-12">
        {sections.map((section, index) => {
          const Icon = section.icon;
          return (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
              className="bg-cardBg rounded-xl p-8 border border-secondary"
            >
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <Icon className="w-6 h-6 mr-3 text-accent" />
                {section.title}
              </h2>
              <ul className="space-y-3">
                {section.content.map((item) => (
                  <li key={item} className="flex items-start text-textMuted">
                    <span className="text-accent mr-3 mt-1">→</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          );
        })}
      </div>

      {/* Premium Services */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8 }}
        className="bg-cardBg rounded-xl p-8 border border-secondary mb-12"
      >
        <h2 className="text-2xl font-bold mb-4">Premium Services</h2>
        <p className="text-textMuted mb-4">
          TypeRush offers optional premium features through subscription plans:
        </p>
        <ul className="space-y-2 text-textMuted mb-4">
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            Premium subscriptions are billed monthly or annually
          </li>
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            Subscriptions auto-renew unless cancelled before the renewal date
          </li>
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            Refunds are processed according to our refund policy (within 7 days of purchase)
          </li>
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            Premium benefits are active only during the subscription period
          </li>
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            We reserve the right to modify premium features and pricing with notice
          </li>
        </ul>
      </motion.div>

      {/* Disclaimer */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1 }}
        className="bg-cardBg rounded-xl p-8 border border-secondary mb-12"
      >
        <h2 className="text-2xl font-bold mb-4 flex items-center">
          <AlertTriangle className="w-6 h-6 mr-3 text-yellow-500" />
          Disclaimer of Warranties
        </h2>
        <p className="text-textMuted mb-4">
          TypeRush is provided "as is" without warranties of any kind. We strive for 100% uptime 
          and accuracy, but cannot guarantee:
        </p>
        <ul className="space-y-2 text-textMuted">
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            Uninterrupted or error-free service
          </li>
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            Accuracy of typing test results in all circumstances
          </li>
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            Security against all potential threats or vulnerabilities
          </li>
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            Compatibility with all devices and browsers
          </li>
        </ul>
      </motion.div>

      {/* Limitation of Liability */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.2 }}
        className="bg-cardBg rounded-xl p-8 border border-secondary mb-12"
      >
        <h2 className="text-2xl font-bold mb-4">Limitation of Liability</h2>
        <p className="text-textMuted mb-4">
          To the maximum extent permitted by law, TypeRush and its team shall not be liable for:
        </p>
        <ul className="space-y-2 text-textMuted mb-4">
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            Loss of data, profits, or business opportunities
          </li>
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            Indirect, incidental, or consequential damages
          </li>
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            Issues arising from third-party services or content
          </li>
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            Damages resulting from unauthorized access to your account
          </li>
        </ul>
        <p className="text-textMuted">
          Our total liability is limited to the amount you paid for premium services (if any) 
          in the 12 months preceding the claim.
        </p>
      </motion.div>

      {/* Changes to Terms */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.4 }}
        className="bg-cardBg rounded-xl p-8 border border-secondary mb-12"
      >
        <h2 className="text-2xl font-bold mb-4">Changes to Terms</h2>
        <p className="text-textMuted mb-4">
          We may revise these Terms of Service at any time. Changes will be effective immediately 
          upon posting. Material changes will be communicated through:
        </p>
        <ul className="space-y-2 text-textMuted mb-4">
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            Email notification to registered users
          </li>
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            Prominent notice on the platform
          </li>
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            Updated "Last Modified" date at the top of this page
          </li>
        </ul>
        <p className="text-textMuted">
          Continued use of TypeRush after changes constitutes acceptance of the revised terms.
        </p>
      </motion.div>

      {/* Contact */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.6 }}
        className="bg-accent/10 border border-accent/20 rounded-xl p-8 text-center"
      >
        <h2 className="text-2xl font-bold mb-4">Questions About These Terms?</h2>
        <p className="text-textMuted mb-6">
          If you have questions or concerns about our Terms of Service, we're here to help.
        </p>
        <div className="text-textMuted">
          <p className="mb-2">Email: <span className="text-accent">legal@typerush.com</span></p>
          <p>Response time: Within 48 hours</p>
        </div>
      </motion.div>
    </div>
  );
};

export default Terms;
